"use client";
import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";

import {
  HeroImage,
  Gradient,
  HeroVideo,
  HeroBannerWrapper,
  EmbedVideo,
  CTAContainer,
} from "./styles";

import homeBg from "@itchannelplus/assets/images/home-bg.png";
import {
  getImage,
  getImageProp,
  getImageSrc,
  getVideoProp,
  ImageProps,
  SimpleButtonProps,
  VideoProps,
  appendQueryParameters,
  FULL_WIDTH,
} from "@itchannelplus/utils";
import { useInView } from "react-intersection-observer";
import { H1 } from "../Typography";
import { ButtonLink } from "../buttons/Button";
import Image from "next/image";
import nl2br from "react-br";
import styles from "./styles.module.scss";

// CTA button on a banner: SimpleButtonProps plus the button styling that the
// banner needs (defaults to light/solid for the first and light/outline after).
export type HeroBannerCta = SimpleButtonProps & {
  color?: "dark" | "light" | "primary" | "secondary";
  variant?: "solid" | "outline";
};

export type HeroBannerProps = {
  title?: string;
  titleSize?: "big" | "medium" | "small" | "xsmall";
  tagline?: string | ReactNode;
  // Short supporting copy. A string is rendered as plain text (nl2br); a
  // ReactNode is rendered as-is — pass markdown rendered upstream in a server
  // component, since the markdown pipeline is async and this is a client one.
  teaser?: string | ReactNode;
  ctaButton?: SimpleButtonProps;
  ctaButtons?: HeroBannerCta[];
  image?: ImageProps;
  video?: VideoProps;
  className?: string;
  style?: CSSProperties;
  contain?: boolean; //whether image or video will cover the entire space
  justify?: 'center' | 'end';
  priority?: boolean;
  // Push the content below the fixed transparent header (for pages whose hero
  // is the first element under the site header).
  headerOffset?: boolean;
  // Overlay over the media. 'small' = bottom scrim, 'large' = full scrim,
  // 'none' = off. When omitted, it auto-picks: 'large' if the copy is long
  // enough to need it, otherwise 'small'.
  gradient?: "none" | "small" | "large";
};

export const HeroBanner = ({
  video,
  image,
  title,
  titleSize = "big",
  tagline,
  teaser,
  ctaButton,
  ctaButtons,
  contain = false,
  justify,
  className,
  style,
  priority,
  headerOffset = false,
  gradient,
}: HeroBannerProps) => {
  // Long copy spreads across the whole photo, so a bottom-only scrim is not
  // enough — switch to the full scrim. ~50 chars or a second line means long.
  const teaserText = typeof teaser === "string" ? teaser : "";
  const isLongCopy =
    teaserText.length > 50 || teaserText.includes("\n") || typeof teaser !== "string";
  const gradientSize = gradient ?? (isLongCopy ? "large" : "small");
  //issue with React Player: https://lightrun.com/solutions/cookpete-react-player-nextjs-react-18-hydration-error/
  const player = useRef<HTMLVideoElement>(undefined);
  //const {isIFrameLoaded, iframeRef} = useIsIFrameLoaded();
  const [isIFrameLoaded, setIsIFrameLoaded] = useState(false);
  const { ref, inView } = useInView({});
  //todo check if banner is active/visible, video and is autoplay is active,
  useEffect(() => {
    if (player.current) {
      if (inView) {
        player.current.play();
      } else {
        player.current.pause();
      }
    }
  }, [inView, player]);
  /*useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.src = appendQueryParameters(getVideoProp('src', video), {background:1, muted:true});
    }
  }, [iframeRef]);*/
  /*
  useEffect(() => {
    if (video && getVideoProp('src', video)?.indexOf('vimeo') > -1) {
      addPrefetch('preconnect','https://player.vimeo.com' );
      // The iframe document and most of its subresources come right off player.vimeo.com
      addPrefetch('preconnect', 'https://player.vimeo.com');
      // Images
      addPrefetch('preconnect', 'https://i.vimeocdn.com');
      // Files .js, .css
      addPrefetch('preconnect', 'https://f.vimeocdn.com');
      // Metrics
      addPrefetch('preconnect', 'https://fresnel.vimeocdn.com');
    }
  }, [video]);
  */

  return (
    <HeroBannerWrapper
      ref={ref}
      className={className}
      style={style}
      contain={contain}
      justify={justify}
      headerOffset={headerOffset}
    >
      {!video && image && (
        <HeroImage
          fill
          priority={priority}
          src={getImageProp("src", image) || homeBg}
          alt={getImageProp("alt", image) || "Le Voyage"}
          title={getImageProp("alt", image)}
          width={getImageProp("width", image)}
          height={getImageProp("height", image)}
          sizes={FULL_WIDTH}
        />
      )}
      {video && !getVideoProp("embed", video) && (
        <HeroVideo
          ref={player as any}
          autoPlay
          loop
          muted
          src={getVideoProp("src", video)}
          poster={getImage(video.poster!) as string}
          width={"100%"}
          height={"100%"}
        />
      )}
      {video && getVideoProp("embed", video) && (
        <EmbedVideo loaded={!video.poster || isIFrameLoaded}>
          {video.poster && (
            <Image
              src={getImageSrc(video.poster)}
              alt={title || video.alt || "Video Poster"}
              fill
              sizes={FULL_WIDTH}
            />
          )}
          <iframe
            src={appendQueryParameters(getVideoProp("src", video), {
              background: 1,
              muted: true,
            })}
            frameBorder={0}
            width="100%"
            height="100%"
            allow="autoplay; fullscreen"
            allowFullScreen
            loading="lazy"
            data-vimeo-defer={true}
            onLoad={() => setTimeout(() => setIsIFrameLoaded(true), 500)}
          />
        </EmbedVideo>
      )}

      {tagline && <span className={styles.heroTagline}>{tagline}</span>}
      {title && <H1 size={titleSize}>{title} </H1>}
      {teaser && (
        <div className={styles.heroTeaser}>
          {typeof teaser === "string" ? nl2br(teaser) : teaser}
        </div>
      )}
      {(ctaButtons || ctaButton) && (
        <CTAContainer>
          {ctaButtons
            ? ctaButtons.map((button, index) => {
                const { label, color, variant, ref: _ref, ...rest } = button;
                return (
                  <ButtonLink
                    key={index}
                    href={rest.href}
                    color={color || "light"}
                    variant={variant || "solid"}
                    {...(rest as any)}
                  >
                    {label}
                  </ButtonLink>
                );
              })
            : ctaButton && (
                <ButtonLink
                  href={ctaButton.href}
                  color={"light"}
                  variant={"solid"}
                >
                  {ctaButton.label}
                </ButtonLink>
              )}
        </CTAContainer>
      )}
      {gradientSize !== "none" && <Gradient size={gradientSize} />}
    </HeroBannerWrapper>
  );
};

export default HeroBanner;
